import React, { useState, useMemo } from 'react';
import { Box, Typography, Chip, Card, CardContent, IconButton, Collapse } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import moment from 'moment';
import BookingDetailsDrawer from './BookingDetailsDrawer';

/**
 * Improved Booking Calendar Room View Component
 * Uses Material-UI components with responsive design
 * Similar to MUI Data Grid time-off calendar demo
 */
const BookingCalendarRoomViewImproved = ({ 
  data, 
  expandedRoomTypes, 
  onExpandedRoomTypesChange, 
  bookingType = 'nightly',
  theme = 'light' 
}) => {
  const { rooms = [], room_types = [], bookings = [], start_date, end_date } = data || {};
  
  const isDark = theme === 'dark';
  
  // Use provided state or fallback to local state
  const [localExpandedRoomTypes, setLocalExpandedRoomTypes] = useState(new Set());
  const expandedRoomTypesState = expandedRoomTypes !== undefined ? expandedRoomTypes : localExpandedRoomTypes;
  const setExpandedRoomTypesState = onExpandedRoomTypesChange !== undefined ? onExpandedRoomTypesChange : setLocalExpandedRoomTypes;
  
  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);

  // Generate date range
  const dates = useMemo(() => {
    if (!start_date || !end_date) return [];
    const datesArray = [];
    const start = moment(start_date);
    const end = moment(end_date);
    const current = moment(start);

    while (current.isSameOrBefore(end, 'day')) {
      datesArray.push({
        key: current.format('YYYY-MM-DD'),
        date: current.clone(),
        display: current.format('ddd'),
        day: current.format('D'),
        month: current.format('MMM'),
      });
      current.add(1, 'day');
    }
    return datesArray;
  }, [start_date, end_date]);

  // Group rooms by room type
  const roomsByType = useMemo(() => {
    const result = {};
    rooms.forEach(room => {
      const typeId = room.room_type_id || 'none';
      if (!result[typeId]) {
        result[typeId] = [];
      }
      result[typeId].push(room);
    });
    return result;
  }, [rooms]);

  // Group bookings by room and date
  // Track which bookings have been assigned to avoid duplicates
  const bookingsByRoomAndDate = useMemo(() => {
    const result = {};
    const assignedBookings = new Set(); // Track bookings already assigned to a room
    
    if (!bookings || !dates.length) return result;

    bookings.forEach(booking => {
      const bookingStart = moment(booking.start || booking.start_date || booking.check_in_date);
      const bookingEnd = moment(booking.end || booking.end_date || booking.check_out_date);
      
      // If booking has allocated rooms, map by room_id
      if (booking.room_ids && booking.room_ids.length > 0) {
        booking.room_ids.forEach(roomId => {
          if (!result[roomId]) {
            result[roomId] = {};
          }
          let dateCheck = moment(bookingStart);

          while (dateCheck.isSameOrBefore(bookingEnd, 'day')) {
            const dateKey = dateCheck.format('YYYY-MM-DD');
            if (dates.find(d => d.key === dateKey)) {
              if (!result[roomId][dateKey]) {
                result[roomId][dateKey] = [];
              }
              // Only add if not already in this room/date
              const exists = result[roomId][dateKey].some(b => b.id === booking.id);
              if (!exists) {
              result[roomId][dateKey].push(booking);
              }
            }
            dateCheck.add(1, 'day');
          }
        });
        assignedBookings.add(booking.id);
      } else if (booking.room_type_ids && booking.room_type_ids.length > 0) {
        // If no allocated rooms, assign to FIRST available room of matching room type only
        // This prevents duplicates - one booking per room type
        booking.room_type_ids.forEach(roomTypeId => {
          // Find first available room of this type that doesn't have this booking yet
          const roomsOfType = rooms.filter(r => r.room_type_id === roomTypeId);
          
          // Find first room where this booking doesn't conflict or is already assigned
          let assigned = false;
          for (const room of roomsOfType) {
            // Check if this booking is already assigned to this room
            const bookingKey = `${booking.id}_${roomTypeId}`;
            if (assignedBookings.has(bookingKey)) {
              assigned = true;
              break;
            }
            
            // Check if room has any overlapping bookings on the same dates
            let hasConflict = false;
            let dateCheck = moment(bookingStart);
            while (dateCheck.isSameOrBefore(bookingEnd, 'day')) {
              const dateKey = dateCheck.format('YYYY-MM-DD');
              if (dates.find(d => d.key === dateKey)) {
                const existingBookings = result[room.id]?.[dateKey] || [];
                // Check for overlapping bookings (same date range)
                const conflict = existingBookings.some(existing => {
                  const existingStart = moment(existing.start || existing.start_date || existing.check_in_date);
                  const existingEnd = moment(existing.end || existing.end_date || existing.check_out_date);
                  return dateCheck.isSameOrAfter(existingStart, 'day') && dateCheck.isSameOrBefore(existingEnd, 'day');
                });
                if (conflict) {
                  hasConflict = true;
                  break;
                }
              }
              dateCheck.add(1, 'day');
            }
            
            // If no conflict, assign to this room
            if (!hasConflict) {
              if (!result[room.id]) {
                result[room.id] = {};
              }
              dateCheck = moment(bookingStart);
              while (dateCheck.isSameOrBefore(bookingEnd, 'day')) {
                const dateKey = dateCheck.format('YYYY-MM-DD');
                if (dates.find(d => d.key === dateKey)) {
                  if (!result[room.id][dateKey]) {
                    result[room.id][dateKey] = [];
                  }
                  result[room.id][dateKey].push(booking);
                }
                dateCheck.add(1, 'day');
              }
              assignedBookings.add(bookingKey);
              assigned = true;
              break;
            }
          }
        });
      }
    });
    return result;
  }, [bookings, dates, rooms]);

  const toggleRoomType = (typeId) => {
    const newExpanded = new Set(expandedRoomTypesState);
    if (newExpanded.has(typeId)) {
      newExpanded.delete(typeId);
    } else {
      newExpanded.add(typeId);
    }
    setExpandedRoomTypesState(newExpanded);
  };

  if (!data || !room_types || room_types.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          color: isDark ? '#999' : '#757575',
        }}
      >
        <Typography variant="body1">No room types available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {room_types.map((roomType) => {
        const roomsInType = roomsByType[roomType.id] || [];
        const isExpanded = expandedRoomTypesState.has(roomType.id);
        const totalBookings = roomsInType.reduce((sum, room) => {
          const roomBookings = bookingsByRoomAndDate[room.id] || {};
          return sum + Object.values(roomBookings).flat().length;
        }, 0);

        return (
          <Card
            key={roomType.id}
            sx={{
              mb: 2,
              backgroundColor: isDark ? '#2a2a31' : '#ffffff',
              border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              borderRadius: 2,
              transition: 'background-color 0.3s ease, border-color 0.3s ease',
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              {/* Room Type Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  p: 1.5, // 12px padding on all sides
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: isDark ? '#333246' : '#f5f5f5',
                  },
                }}
                onClick={() => toggleRoomType(roomType.id)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <IconButton size="small" sx={{ color: isDark ? '#ddd' : '#212121' }}>
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: isDark ? '#ddd' : '#212121',
                      fontSize: '1rem',
                    }}
                  >
                    {roomType.name || `Room Type ${roomType.id}`}
                  </Typography>
                  <Chip
                    label={`${roomsInType.length} rooms`}
                    size="small"
                    sx={{
                      backgroundColor: isDark ? '#333246' : '#f5f5f5',
                      color: isDark ? '#ddd' : '#212121',
                    }}
                  />
                  {totalBookings > 0 && (
                    <Chip
                      label={`${totalBookings} bookings`}
                      size="small"
                      color="primary"
                    />
                  )}
                </Box>
              </Box>

              {/* Rooms Grid - Collapsible */}
              <Collapse in={isExpanded}>
                <Box sx={{ mt: 2, overflowX: 'auto' }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `150px repeat(${dates.length}, minmax(100px, 1fr))`,
                      minWidth: 'fit-content',
                      '@media (min-width: 600px)': {
                        gridTemplateColumns: `150px repeat(${dates.length}, minmax(120px, 1fr))`,
                      },
                    }}
                  >
                    {/* Date Header */}
                    <Box
                      sx={{
                        backgroundColor: isDark ? '#333246' : '#f5f5f5',
                        borderRight: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                        borderBottom: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                        p: 1.5, // 12px padding on all sides
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        color: isDark ? '#ddd' : '#646777',
                        textTransform: 'uppercase',
                      }}
                    >
                      Room
                    </Box>
                    {dates.map(({ key, display, day, month }) => (
                      <Box
                        key={key}
                        sx={{
                          borderRight: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                          borderBottom: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                          p: 1.5, // 12px padding on all sides
                          backgroundColor: isDark ? '#333246' : '#f5f5f5',
                          textAlign: 'center',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          color: isDark ? '#ddd' : '#646777',
                          textTransform: 'uppercase',
                        }}
                      >
                        <Box sx={{ fontSize: '0.875rem' }}>{display}</Box>
                        <Box sx={{ fontSize: '0.7rem', opacity: 0.8 }}>{day}</Box>
                      </Box>
                    ))}

                    {/* Room Rows */}
                    {roomsInType.map((room) => (
                      <React.Fragment key={room.id}>
                        <Box
                          sx={{
                            backgroundColor: isDark ? '#2a2a31' : '#fafafa',
                            borderRight: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                            borderBottom: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                            p: 1.5,
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            color: isDark ? '#ddd' : '#212121',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {room.room_number || `Room ${room.id}`}
                        </Box>
                        {dates.map(({ key }, dateIndex) => {
                          const roomBookings = bookingsByRoomAndDate[room.id]?.[key] || [];
                          
                          // Get unique bookings for this date (avoid duplicates)
                          const uniqueBookings = [];
                          const seenBookingIds = new Set();
                          roomBookings.forEach(booking => {
                            if (!seenBookingIds.has(booking.id)) {
                              seenBookingIds.add(booking.id);
                              uniqueBookings.push(booking);
                            }
                          });
                          
                          return (
                            <Box
                              key={key}
                              sx={{
                                borderRight: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                                borderBottom: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                                p: 0.5,
                                minHeight: 50,
                                height: 50,
                                backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                                position: 'relative',
                                overflow: 'visible',
                                '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  borderRight: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                                  borderBottom: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                                  pointerEvents: 'none',
                                  zIndex: 0,
                                },
                              }}
                            >
                              {uniqueBookings.map((booking, index) => {
                                const bookingStart = moment(booking.start || booking.start_date || booking.check_in_date);
                                const bookingEnd = moment(booking.end || booking.end_date || booking.check_out_date);
                                const currentDate = moment(key);
                                
                                // Calculate if this is the start, middle, or end of the booking
                                const isStart = currentDate.isSame(bookingStart, 'day');
                                const isEnd = currentDate.isSame(bookingEnd, 'day');
                                const isMiddle = currentDate.isAfter(bookingStart, 'day') && currentDate.isBefore(bookingEnd, 'day');
                                const isSingleDay = isStart && isEnd;
                                
                                // Only show booking info on the start date (or single day)
                                const showBookingInfo = isStart || isSingleDay;
                                
                                // Calculate width and position for connected bars
                                // All bars have same width, starting from middle of start date and ending in middle of end date
                                let left = '0%';
                                let width = '100%';
                                let borderRadius = '38px';
                                
                                if (isSingleDay) {
                                  // Single day booking - centered (25% to 75%)
                                  left = '25%';
                                  width = '50%';
                                  borderRadius = '38px';
                                } else if (isStart && !isEnd) {
                                  // Start of booking - starts from middle (50% to 100%)
                                  left = '50%';
                                  width = '50%';
                                  borderRadius = '38px 0 0 38px';
                                } else if (isEnd && !isStart) {
                                  // End of booking - ends in middle (0% to 50%)
                                  left = '0%';
                                  width = '50%';
                                  borderRadius = '0 38px 38px 0';
                                } else if (isMiddle) {
                                  // Middle of booking - full width
                                  left = '0%';
                                  width = '100%';
                                  borderRadius = '0';
                                }
                                
                                // Check if booking continues to next date
                                const continuesNext = dateIndex < dates.length - 1 && 
                                  currentDate.isBefore(bookingEnd, 'day');
                                const continuesPrev = dateIndex > 0 && 
                                  currentDate.isAfter(bookingStart, 'day');
                                
                                // Determine status-based background color
                                const isBlocked = ['blocked', 'reserved', 'block'].includes(booking.status?.toLowerCase());
                                let backgroundColor = '#ff9800'; // default orange
                                if (isBlocked) {
                                  backgroundColor = '#f44336'; // red for blocked
                                } else if (booking.status === 'checked_in') {
                                  backgroundColor = '#4caf50'; // green for checked in
                                } else if (booking.status === 'confirmed') {
                                  backgroundColor = '#2196f3'; // blue for booked/confirmed
                                }
                                
                                // Calculate vertical position for multiple bookings (stack them)
                                // Center the bar vertically in the cell (accounting for padding)
                                const topOffset = index * 50 + 2; // 2px for padding
                                
                                return (
                                  <Box
                                    key={`${booking.id}_${key}`}
                                    sx={{
                                      position: 'absolute',
                                      top: `${topOffset}px`,
                                      left: left,
                                      width: width,
                                      height: '46px', // Match cell height (50px - 4px padding)
                                      zIndex: 10,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        backgroundColor: backgroundColor,
                                        color: '#ffffff',
                                        borderRadius: borderRadius,
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        width: '100%',
                                        height: '100%',
                                        boxSizing: 'border-box',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        padding: '4px 8px',
                                        zIndex: 10,
                                        // Connect to next cell if booking continues
                                        ...(continuesNext && {
                                          borderRight: 'none',
                                          marginRight: '-1px',
                                        }),
                                        // Connect to previous cell if booking continues
                                        ...(continuesPrev && {
                                          marginLeft: '-1px',
                                        }),
                                      }}
                                      onClick={() => {
                                        setSelectedBookingId(booking.id);
                                        setDrawerOpen(true);
                                      }}
                                    >
                                      {showBookingInfo && (
                                        <Box sx={{ fontSize: '0.7rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', lineHeight: 1.2 }}>
                                        {booking.guest_name || 'Guest'}
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        );
      })}
      
      {/* Booking Details Drawer */}
      <BookingDetailsDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedBookingId(null);
        }}
        bookingId={selectedBookingId}
        theme={theme}
      />
    </Box>
  );
};

export default BookingCalendarRoomViewImproved;
